import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import {
  markInboxRead,
  subscribeInboxes,
  type InboxEntry,
} from '../lib/repositories/inbox-repo.js';
import { useInboxPaths } from '../lib/use-inbox-paths.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX } from '../lib/layout.js';
import { formatRelativeTime, type InboxMessage } from '@kilat/shared';
import {
  Badge,
  Button,
  PushPermissionCard,
  backLinkStyle,
  backButtonReset,
  localizedInboxText,
  usePushRegistration,
} from '@kilat/ui';

/**
 * /inbox — the READER for the operator's `users/{uid}/inbox`, merged with the
 * brand's shared super-admin queue for a super-admin (see inbox-repo.ts).
 *
 * The operator-facing growth engine (Daily Money Moment digest, first-win
 * celebration, streak-loss nudge) delivers in-app into `users/{ownerUid}/inbox`
 * via the admin SDK; until now nothing rendered it. This screen does: newest-
 * first, unread-dotted, tap → mark read (+ open the deep link).
 *
 * Bounded read (orderBy createdAt desc, limit N). No-dead-ends: explicit
 * loading / signed-out / error / empty / list states. Kilat-branded (tokens).
 *
 * Also the home of the operator's push control ("Notifications on this
 * device") — the place someone looks when they wonder why a booking did not
 * ping them — and "Mark all as read", which clears the bell in one write.
 */
export function InboxPage() {
  const { user, loading: authLoading } = useAuth();
  const t = useI18n();
  const nav = useNavigate();

  /**
   * Step back through history, or land on the queue when there is none.
   *
   * `history.length <= 1` is the cold-load case: a deep link, a refresh, or a
   * notification tap that opened the app straight here. Calling `nav(-1)`
   * there leaves the operator on a blank tab.
   */
  function goBack(): void {
    if (window.history.length > 1) nav(-1);
    else nav('/queue');
  }

  const uid = user?.uid ?? null;
  const paths = useInboxPaths();
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const subscribe = useMemo(() => {
    if (!paths) return null;
    return (cb: (vs: InboxEntry[]) => void) =>
      subscribeInboxes(
        paths,
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths, retryNonce]);

  const {
    data: entries,
    loading,
    error: snapError,
  } = useTrackedSnapshot<InboxEntry[]>(['inbox', paths?.join('|') ?? null, retryNonce], subscribe);
  const inboxError = loadError ?? snapError;
  const [markingAll, setMarkingAll] = useState(false);
  const [markAllFailed, setMarkAllFailed] = useState(false);

  async function markAll(unread: readonly InboxEntry[]): Promise<void> {
    if (unread.length === 0) return;
    setMarkingAll(true);
    setMarkAllFailed(false);
    try {
      await markInboxRead(unread);
    } catch (e) {
      logger.warn('inbox mark-all-read failed', { count: unread.length, error: String(e) });
      setMarkAllFailed(true);
    } finally {
      setMarkingAll(false);
    }
  }

  function open(entry: InboxEntry) {
    const m = entry.message;
    if (!m.readAt) {
      void markInboxRead([entry]).catch((e: unknown) =>
        logger.warn('inbox mark-read failed', { id: m.id, error: String(e) }),
      );
    }
    if (m.deepLink && m.deepLink.startsWith('/')) nav(m.deepLink);
  }

  if (authLoading) {
    return (
      <Centered
        title={t.t('dashboard.inbox.loadingTitle')}
        body={t.t('dashboard.inbox.loadingBody')}
      />
    );
  }
  if (!uid) {
    return (
      <Centered
        title={t.t('dashboard.inbox.signedOutTitle')}
        body={t.t('dashboard.inbox.signedOutBody')}
        cta={{ label: t.t('dashboard.states.backToQueue'), to: '/queue' }}
      />
    );
  }

  const list = entries ?? [];
  const unread = list.filter((e) => !e.message.readAt);
  const nowMs = Date.now();

  return (
    <main
      style={{
        maxWidth: PAGE_FRAME_MAX,
        margin: '0 auto',
        padding: 'var(--space-6) var(--space-5)',
        minHeight: '100dvh',
      }}
    >
      {/* Title on the left, this device's push status beside it: on a wide
          screen the push card is a compact side panel rather than a full-width
          block pushing the messages down; on a phone it stacks under the
          subtitle exactly as before. */}
      <div className="layout-split-aside" style={{ marginBottom: 'var(--space-5)' }}>
        <div>
          {/* A real back, not a link to the queue wearing an arrow.
              `/inbox` is reached from the bell in the nav, from ANY screen — so an
              operator who opened it from `/reports` and pressed "← Back to queue"
              landed somewhere they had not been. History first; the queue is the
              fallback for a cold load, where there is no history to go back to. */}
          <button type="button" onClick={goBack} style={{ ...backLinkStyle, ...backButtonReset }}>
            ← {t.t('common.back')}
          </button>
          <h1
            style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 'var(--space-3) 0 0 0' }}
          >
            {t.t('dashboard.inbox.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 'var(--space-2) 0 0 0',
              lineHeight: 1.5,
            }}
          >
            {t.t('dashboard.inbox.subtitle')}
          </p>
        </div>
        <DevicePushCard />
      </div>

      {!loading && !inboxError && unread.length > 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-3)',
          }}
        >
          <span
            role="status"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
          >
            {t.t('common.inbox.unreadSummary', { count: unread.length })}
          </span>
          <Button variant="secondary" disabled={markingAll} onClick={() => void markAll(unread)}>
            {markingAll ? t.t('common.inbox.markingAllRead') : t.t('common.inbox.markAllRead')}
          </Button>
        </div>
      ) : null}
      {markAllFailed ? (
        <p
          role="alert"
          style={{
            margin: '0 0 var(--space-3) 0',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-danger-bg)',
            fontSize: 'var(--text-meta)',
          }}
        >
          {t.t('common.inbox.markAllFailed')}
        </p>
      ) : null}

      {loading ? (
        <Skeleton />
      ) : inboxError ? (
        <ErrorState
          title={t.t('dashboard.inbox.errorTitle')}
          body={t.t('dashboard.inbox.errorBody')}
          retryLabel={t.t('dashboard.inbox.retry')}
          onRetry={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
        />
      ) : list.length === 0 ? (
        <EmptyState
          title={t.t('dashboard.inbox.emptyTitle')}
          body={t.t('dashboard.inbox.emptyBody')}
        />
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {list.map((entry) => {
            const m = entry.message;
            return (
              <li key={`${entry.path}/${m.id}`}>
                <MessageRow
                  message={m}
                  when={formatRelativeTime(tsMs(m.createdAt), nowMs, t.locale)}
                  kindLabel={
                    m.kind === 'transactional'
                      ? { text: t.t('common.inbox.kindUpdate'), tone: 'info' }
                      : m.kind === 'milestone'
                        ? { text: t.t('common.inbox.kindMilestone'), tone: 'success' }
                        : { text: t.t('common.inbox.kindNote'), tone: 'neutral' }
                  }
                  locale={t.locale}
                  openAriaLabel={t.t('dashboard.inbox.openAria', {
                    title: localizedInboxText(m, t.locale).title,
                  })}
                  onOpen={() => open(entry)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}

// ── helpers ───────────────────────────────────────────────────────────

/** Firestore Timestamp → epoch ms, tolerant of bare `{ seconds }` fixtures
 *  (the shared `Timestamp` type marks `toMillis` optional). */
function tsMs(ts: { toMillis?: () => number; seconds: number }): number {
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

// ── row ───────────────────────────────────────────────────────────────

function MessageRow({
  message,
  when,
  kindLabel,
  locale,
  openAriaLabel,
  onOpen,
}: {
  readonly message: InboxMessage;
  readonly when: string;
  readonly kindLabel: { readonly text: string; readonly tone: 'info' | 'success' | 'neutral' };
  readonly locale: string;
  readonly openAriaLabel: string;
  readonly onOpen: () => void;
}) {
  const unread = !message.readAt;
  // Viewer-locale copy when the server sent the all-locale maps.
  const text = localizedInboxText(message, locale);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={openAriaLabel}
      style={{
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        color: 'inherit',
        background: unread ? 'rgba(13,122,79,0.05)' : 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-1)',
        padding: 'var(--space-4)',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 'var(--space-3)',
        alignItems: 'start',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          marginTop: 6,
          borderRadius: 'var(--radius-pill)',
          background: unread ? 'var(--color-primary)' : 'transparent',
        }}
      />
      <span style={{ minWidth: 0 }}>
        <span
          style={{ display: 'block', fontWeight: unread ? 600 : 500, overflowWrap: 'anywhere' }}
        >
          {text.title}
        </span>
        <span
          style={{
            display: 'block',
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
            overflowWrap: 'anywhere',
          }}
        >
          {text.body}
        </span>
      </span>
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 'var(--space-2)',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          whiteSpace: 'nowrap',
        }}
      >
        {when}
        <Badge tone={kindLabel.tone}>{kindLabel.text}</Badge>
      </span>
    </button>
  );
}

// ── states ────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div aria-hidden style={{ display: 'grid', gap: 'var(--space-2)' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 76,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface-muted, var(--tint-subtle))',
          }}
        />
      ))}
    </div>
  );
}

function EmptyState({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: 'var(--space-8) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
        justifyItems: 'center',
      }}
    >
      <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 420,
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
    </div>
  );
}

function ErrorState({
  title,
  body,
  retryLabel,
  onRetry,
}: {
  readonly title: string;
  readonly body: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      style={{
        textAlign: 'center',
        padding: 'var(--space-8) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
        justifyItems: 'center',
      }}
    >
      <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 420,
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          minHeight: 'var(--touch-target)',
          padding: 'var(--space-3) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-primary)',
          color: 'var(--color-primary-fg)',
          border: 'none',
          fontWeight: 600,
          fontSize: 'var(--text-body)',
          cursor: 'pointer',
          boxShadow: 'var(--shadow-2)',
        }}
      >
        {retryLabel}
      </button>
    </div>
  );
}

function Centered({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 'var(--space-8) var(--space-5)',
      }}
    >
      <div
        style={{ display: 'grid', gap: 'var(--space-3)', justifyItems: 'center', maxWidth: 420 }}
      >
        <p style={{ margin: 0, fontWeight: 500 }}>{title}</p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {body}
        </p>
        {cta ? (
          <Link
            to={cta.to}
            style={{
              padding: '14px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
              boxShadow: 'var(--shadow-2)',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

/**
 * Operator push for THIS browser/device: bookings, payments, billing and the
 * daily digest. Shares state with the app-wide foreground toast.
 */
function DevicePushCard() {
  const t = useI18n();
  const push = usePushRegistration();
  return (
    <PushPermissionCard
      state={push.state}
      busy={push.busy}
      registrationFailed={push.registrationFailed}
      onEnable={() => void push.enable()}
      copy={{
        title: t.t('common.push.cardTitle'),
        checking: t.t('common.push.checking'),
        on: { status: t.t('common.push.onStatus'), body: t.t('dashboard.push.onBody') },
        ask: {
          status: t.t('common.push.askStatus'),
          body: t.t('dashboard.push.askBody'),
          action: t.t('common.push.enableAction'),
          busyAction: t.t('common.push.enableBusy'),
        },
        blocked: { status: t.t('common.push.blockedStatus'), body: t.t('common.push.blockedBody') },
        iosInstall: { status: t.t('common.push.iosStatus'), body: t.t('common.push.iosBody') },
        unsupported: {
          status: t.t('common.push.unsupportedStatus'),
          body: t.t('common.push.unsupportedBody'),
        },
        registrationFailed: t.t('common.push.registrationFailed'),
        retry: t.t('common.push.retry'),
      }}
    />
  );
}
